import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import AppBar from '../../../../components/AppBar';
import Layout from '../../../../components/Layout';
import TabsLinks from '../../../../components/TabsLinks';
import { useApi } from '../../../../hooks/useApi';
import { paths } from '../../../../routes';
import ChatList from './components/ChatList';
import ChatContent from './components/ChatContent';
import { StyledChat, StyledChatContent } from './styled';
import { ApiChat } from '../../../../api/chat';
import Typography from '../../../../components/Typography';
import useTheme from '../../../../hooks/useTheme';
import { useMemo } from 'react';
import { filterDataByString, findByID, parseChatCandidates, reverseArray } from '../../../../utils/helpers';
import { useDispatch, useSelector } from 'react-redux';
import { clearNewMessages } from '../../../../redux/notifications/actions';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

function chatReducer(state, action) {
    switch (action.type) {
        case 'set_state': {
            return { user: action.payload?.user, start: action.payload?.start, messages: action.payload?.messages }
        }
        case 'set_start': {
            return { ...state, start: action.payload }
        }
        case 'set_messages': {
            return { ...state, messages: action.payload }
        }
        default: {
            throw new Error(`Unhandled action type: ${action.type}`)
        }
    }
};

const Chat = () => {
    const { t } = useTranslation()
    const dispatch = useDispatch();
    const theme = useTheme();
    const history = useHistory();
    const { id } = useParams();

    const limit = useMemo(() => 10, []);
    const [{ user, start, messages }, chatDispatch] = useReducer(chatReducer, { user: null, start: limit, messages: [] });
    const [searchText, setSearchValue] = useState("");
    const [showUnreadMessages, setShowUnreadMessages] = useState(false);

    const refMessagesScroll = useRef(null);

    const [{ data: chatCandidates, loading: chatCandidatesLoading }, getChatCandidates] = useApi(ApiChat.getChatCandidates);
    const [{ loading: chatByUserLoading }, getChatByUserId] = useApi(ApiChat.getChatByUserId, { manual: true });
    const [{ loading: sendToCandidateLoading }, sendToCandidate] = useApi(ApiChat.sendToCandidate, { manual: true });

    // Memoize data
    const chatCandidatesParsed = useMemo(
        () => parseChatCandidates(chatCandidates),
        [chatCandidates]
    );

    const chatCandidatesFiltered = useMemo(
        () => filterDataByString(chatCandidatesParsed, searchText, candidate => candidate?.username),
        [chatCandidatesParsed, searchText]
    );

    const chatCandidatesFilteredByUnread = useMemo(
        () => showUnreadMessages ? chatCandidatesFiltered.filter(candidate => candidate?.is_unread) : chatCandidatesFiltered,
        [chatCandidatesFiltered, showUnreadMessages]
    );

    const newMessages = useSelector(state => state.notifications.newMessages);
    const isMoreMessages = useMemo(() => !!(messages && messages.length > 0 && start <= messages?.length), [start, messages]);
    const isNoMessages = useMemo(() => !!(messages && messages.length === 0), [messages]);

    const scrollMessagesToBottom = useCallback(
        () => refMessagesScroll.current.scrollTop = refMessagesScroll.current.scrollHeight,
        []
    );

    const scrollMessagesToTop = useCallback(
        () => refMessagesScroll.current.scrollTop = 100,
        []
    );

    // Set user if not exists or change from url
    useEffect(() => {
        if (chatCandidatesParsed && chatCandidatesParsed.length > 0) {
            if (!user || parseInt(user?.id, 10) !== parseInt(id, 10)) {
                const findedUser = findByID(chatCandidatesParsed, id);
                if (id && findedUser) {
                    chatDispatch({ type: 'set_state', payload: { user: findedUser, start: limit, messages: [] } });
                } else {
                    const [firstUser] = chatCandidatesParsed;
                    if (firstUser?.id) {
                        chatDispatch({ type: 'set_state', payload: { user: firstUser, start: limit, messages: [] } });
                        history.replace(`${paths.chat}/${firstUser.id}`);
                    }
                }
            }
        }
    }, [user, id, limit, chatCandidatesParsed, history]);

    // Update messages or candidates if exists new message
    useEffect(() => {
        if (newMessages && newMessages.length > 0) {
            const isCurrentChat = !!newMessages.find(newMessage => parseInt(newMessage?.userId, 10) === parseInt(user?.id, 10));
            if (isCurrentChat) {
                const async = async () => {
                    const res = await getChatByUserId(user.id, 0, start);
                    if (res?.data) {
                        chatDispatch({ type: 'set_messages', payload: reverseArray(res.data) });
                        dispatch(clearNewMessages());
                        scrollMessagesToBottom();
                    }
                }
                async();
            } else {
                const async = async () => {
                    const res = await getChatCandidates();
                    if (res?.data) {
                        dispatch(clearNewMessages());
                    }
                }
                async();
            }
        }
    }, [user, start, newMessages, getChatByUserId, getChatCandidates, scrollMessagesToBottom, dispatch]);

    // Get message if change user or increase limit messages
    useEffect(() => {
        if (user?.id) {
            const async = async () => {
                const res = await getChatByUserId(user.id, 0, start);
                if (user?.is_unread && start === 10) { //Update chat candidates if current user has unread messages
                    getChatCandidates();
                }
                if (res?.data) {
                    chatDispatch({ type: 'set_messages', payload: reverseArray(res.data) });
                    if (start === 10) {
                        scrollMessagesToBottom();
                    } else {
                        scrollMessagesToTop();
                    }
                }
            }
            async();
        }
    }, [user, start, limit, getChatByUserId, getChatCandidates, scrollMessagesToBottom, scrollMessagesToTop, history]);

    const handleChangeSearch = e => {
        setSearchValue(e.target.value);
    }

    const handleClickUser = (id) => () => {
        history.replace(`${paths.chat}/${id}`);
    }

    const handleClickUnreadMessages = () => {
        setShowUnreadMessages(prev => !prev);
    }

    const handleScrollMessages = async (e) => {
        const top = e.target.scrollTop;
        if (top < 10) {
            if (!chatByUserLoading && isMoreMessages) {
                chatDispatch({ type: 'set_start', payload: start + limit });
            }
        }
    }

    const handleSubmitMessage = reset => async ({ message }) => {
        if (user?.id && !sendToCandidateLoading) {
            const resSendToCandidate = await sendToCandidate(user.id.toString(), message);
            if (resSendToCandidate?.data) {
                reset();
                const resGetChatByUserId = await getChatByUserId(user.id, 0, start);
                if (resGetChatByUserId?.data) {
                    chatDispatch({ type: 'set_messages', payload: reverseArray(resGetChatByUserId?.data) });
                    scrollMessagesToBottom();
                }
            }
        }
    }

    return (
        <StyledChat>
            <AppBar>
                <Typography
                    font={theme.fonts.mulishRegular}
                    as="h2"
                >
                    {t("Chat")}
                </Typography>
            </AppBar>
            <TabsLinks
                tabs={[{
                    to: paths.chat,
                    name: t('Czaty aktywne')
                }]}
            />
            <Layout>
                <StyledChatContent>
                    <Row>
                        <Col xs={4}>
                            <ChatList
                                activeUser={user}
                                users={chatCandidatesFilteredByUnread}
                                searchText={searchText}
                                showLoader={(chatCandidatesLoading && messages.length === 0)}
                                showUnreadMessages={showUnreadMessages}
                                onClickUser={handleClickUser}
                                onChangeSearch={handleChangeSearch}
                                onClickUnreadMessages={handleClickUnreadMessages}
                            />
                        </Col>
                        <Col xs={8}>
                            <ChatContent
                                ref={refMessagesScroll}
                                user={user}
                                messages={messages}
                                isMoreMessages={isMoreMessages}
                                isNoMessages={isNoMessages}
                                showLoader={(chatByUserLoading && messages && messages.length === 0 && !newMessages?.length > 0)}
                                loading={chatByUserLoading}
                                disabled={sendToCandidateLoading}
                                onScroll={handleScrollMessages}
                                onSubmit={handleSubmitMessage}
                            />
                        </Col>
                    </Row>
                </StyledChatContent>
            </Layout>
        </StyledChat>
    )
}

export default Chat;